import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { login } from "../services/auth.service";
import { saveAccessToken, saveRefreshToken } from "../utils/storage.utils";

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: login,
    onSuccess: async (data) => {
      // When 2FA is required, the backend returns a short-lived pending-scope
      // token in access_token and an empty refresh_token. Do not persist it —
      // the caller will hold it in local state and pass it to the 2FA verify
      // endpoint. Persisting it would attach a pending-scope token to every
      // request via the axios interceptor, which the backend rejects.
      // Never persist a pending-scope token. Guard on both the explicit flag
      // AND an empty refresh_token (the backend contract for pending tokens),
      // so a malformed response where requires_2fa is absent cannot cause a
      // pending-scope token to land in localStorage.
      if (data.requires_2fa === true || data.refresh_token === "") {
        return;
      }

      // Save tokens to Office.js storage. Wrap in try/catch so a storage
      // failure (private mode, quota) surfaces a toast and aborts before
      // invalidateQueries — matches the pattern in useVerify2fa.
      try {
        if (data.access_token) {
          await saveAccessToken(data.access_token);
        }
        if (data.refresh_token) {
          await saveRefreshToken(data.refresh_token);
        }
      } catch {
        toast.error("Could not save your session. Please try again.");
        return;
      }

      // Invalidate user query to trigger refetch and navigate to authenticated screen
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error) => {
      console.error("Login failed:", error);

      // Extract error message from API response
      let errorMessage = "An error occurred during login. Please try again.";

      const data: unknown = isAxiosError(error) ? error.response?.data : undefined;
      if (
        typeof data === "object" &&
        data != null &&
        "detail" in data &&
        typeof data.detail === "string"
      ) {
        errorMessage = data.detail;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Show error toast
      toast.error(errorMessage);
    },
  });
};

export default useLogin;
